<template>
  <v-container>
    <v-toolbar flat>
      <MealAutocomplete
        label="Search for a meal"
        :meal.sync="meal"
        v-bind:key="meal && meal.id"
      />
      <v-spacer/>
      <v-text-field
        label="Lookup by ID"
        v-model="lookupById"
        @keyup.enter="loadMeal(lookupById)"
      />
      <v-btn text outlined class="ml-4" @click="openMissingDialog">
        Find meals with no instructions
      </v-btn>
    </v-toolbar>

    <v-dialog v-model="missingDialog" max-width="1000" scrollable>
      <v-card>
        <v-card-title>
          Find meals with no instructions
          <v-spacer/>
          <v-btn icon @click="missingDialog=false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense align="center">
            <v-col cols="12" sm="3">
              <v-text-field
                type="date"
                label="Meal date from"
                v-model="missingOptions.dateFrom"
                clearable
                hint="Leave blank for no lower limit"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                type="date"
                label="Meal date to"
                v-model="missingOptions.dateTo"
                clearable
                hint="Leave blank to include all future meals"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                label="Also include all meals with diet"
                v-model="missingOptions.includeDiet"
                :items="dietItems"
                item-text="name"
                item-value="id"
                clearable
                hint="Included regardless of date"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn color="primary" block :loading="missingLoading" @click="findMealsWithNoInstructions">
                Search
              </v-btn>
            </v-col>
          </v-row>

          <v-alert v-if="missingError" type="error" text class="mt-4">
            Search failed: {{ missingError }}
          </v-alert>

          <div v-if="missingSearched && !missingLoading" class="mt-4 caption">
            <span v-if="missingCount === 0">No meals found without instructions.</span>
            <span v-else-if="missingCount > missingMeals.length">
              Showing the first {{ missingMeals.length }} of {{ missingCount }} meals. Narrow the dates to see the rest.
            </span>
            <span v-else>{{ missingCount }} meals without instructions.</span>
          </div>

          <v-data-table
            v-if="missingMeals.length > 0"
            class="mt-2"
            dense
            :headers="missingHeaders"
            :items="missingMeals"
            :items-per-page="25"
            :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
            @click:row="selectMissingMeal"
            item-key="id"
          >
            <template v-slot:item.date="{ item }">
              {{ item.date ? getDateString(item.date) : '' }}
            </template>
            <template v-slot:item.diet="{ item }">
              {{ getDietName(item.diet) }}
            </template>
            <template v-slot:item.is_selected="{ item }">
              <v-icon v-if="item.is_selected" small>mdi-star</v-icon>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-alert v-if="loadError" type="error" text>
      Could not find meal {{ mealId }}
    </v-alert>

    <v-card v-if="meal" flat outlined>
      <v-card-title>
        {{ meal.name }}
        <span class="ml-2 text--secondary subtitle-1">
          ({{ getDietName(meal.diet) }}, {{ getDateString(meal.date) }})
        </span>
        <v-spacer/>
        <router-link :to="{ name: 'MealDetail', params: { id: `${meal.id}` }}">
          M{{ meal.id }}
        </router-link>
        <span v-if="meal.sku" class="mx-2">/</span>
        <MealSkuLink :sku="meal.sku"/>
      </v-card-title>
      <v-card-text>
        <InstructionEditor
          v-bind:key="meal.id"
          :meal="meal"
          :read-only="readOnly"
        />
      </v-card-text>
    </v-card>

    <v-alert v-else-if="!loading && !loadError" type="info" text>
      Search for a meal by name or enter its ID to edit the customer prep instructions.
    </v-alert>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import {mapActions, mapGetters} from 'vuex';
import {getDateString} from '@/store/utils';
import MealAutocomplete from '@/components/MealAutocomplete';
import InstructionEditor from '@/components/InstructionEditor';
import MealSkuLink from '@/components/MealSkuLink';

export default {
  name: 'CustomerMealInstructions',
  components: {MealAutocomplete, InstructionEditor, MealSkuLink},
  props: {
    readOnly: {type: Boolean, default: false}
  },
  data() {
    return {
      meal: null,
      lookupById: null,
      loading: false,
      loadError: false,
      missingDialog: false,
      missingOptions: {
        dateFrom: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        dateTo: null,
        includeDiet: null,
      },
      missingLoading: false,
      missingSearched: false,
      missingError: null,
      missingCount: 0,
      missingMeals: [],
      missingHeaders: [
        {text: 'Date', value: 'date'},
        {text: 'Diet', value: 'diet'},
        {text: 'Name', value: 'name'},
        {text: 'SKU', value: 'sku'},
        {text: 'Starred', value: 'is_selected', sortable: false},
        {text: 'ID', value: 'id'},
      ],
    }
  },
  async created() {
    await this.fetchDiets();
    this.missingOptions.includeDiet = this.chefsChoiceDietId;
    if (this.mealId) {
      this.lookupById = this.mealId;
      await this.loadMeal(this.mealId);
    }
  },
  watch: {
    meal(val) {
      const id = (val && val.id) || null;
      if (id) {
        this.lookupById = id;
      }
      if (`${this.mealId || ''}` !== `${id || ''}`) {
        this.$router.push({params: {id}});
      }
      if (val && !Array.isArray(val.instructions)) {
        this.$set(val, 'instructions', []);
      }
    },
    '$route.params.id'(id) {
      if (id && !(this.meal && `${this.meal.id}` === `${id}`)) {
        this.loadMeal(id);
      }
    }
  },
  computed: {
    ...mapGetters(['getDietName', 'diets']),
    mealId() {
      return this.$route.params.id;
    },
    dietItems() {
      return Object.values(this.diets || {})
        .map(d => ({id: d.id, name: d.name}))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    chefsChoiceDietId() {
      const chefsChoice = this.dietItems.find(d => /chef/i.test(d.name));
      return chefsChoice ? chefsChoice.id : null;
    }
  },
  methods: {
    ...mapActions(['fetchDiets']),
    getDateString,
    async loadMeal(id) {
      if (!id) {
        return;
      }
      this.loading = true;
      this.loadError = false;
      try {
        const {data} = await api.get(`v2/meal/${id}`);
        this.meal = data;
      } catch (e) {
        console.warn('failed to load meal', id, e);
        this.meal = null;
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    openMissingDialog() {
      this.missingDialog = true;
      // re-run on every open so meals that just had instructions saved drop off the list
      this.findMealsWithNoInstructions();
    },
    missingMealsWhere() {
      const {dateFrom, dateTo, includeDiet} = this.missingOptions;
      const noInstructions = {
        OR: [
          {instructions: {isEmpty: true}},
          {instructions: {equals: ['']}}
        ]
      };

      const dateRange = {};
      if (dateFrom) {
        dateRange.gte = moment(dateFrom).startOf('day').toISOString();
      }
      if (dateTo) {
        dateRange.lte = moment(dateTo).endOf('day').toISOString();
      }

      const scope = [];
      if (Object.keys(dateRange).length > 0) {
        scope.push({date: dateRange});
      }
      if (includeDiet) {
        scope.push({diet: includeDiet});
      }

      if (scope.length === 0) {
        return noInstructions;
      }
      return {AND: [noInstructions, {OR: scope}]};
    },
    async findMealsWithNoInstructions() {
      this.missingLoading = true;
      this.missingError = null;
      try {
        const params = {
          where: this.missingMealsWhere(),
          orderBy: [{date: {sort: 'desc', nulls: 'last'}}, {id: 'desc'}],
          minimal: true,
          limit: 500
        };
        const {data} = await api.get('/v2/meal', {params});
        this.missingCount = data.count;
        this.missingMeals = data.meals;
        this.missingSearched = true;
      } catch (e) {
        console.warn('failed to find meals with no instructions', e);
        this.missingError = (e.response && e.response.data && e.response.data.message) || e.message;
      } finally {
        this.missingLoading = false;
      }
    },
    selectMissingMeal(item) {
      this.missingDialog = false;
      this.loadMeal(item.id);
    }
  }
}
</script>
